import { useState, useEffect } from 'react';
import { api } from '@/shared/lib/api';

interface LootDropItem {
  name: string;
  name_ru?: string;
  description: string;
  description_ru?: string;
  rarity: string;
  slot: string;
  flavor_text: string;
}

interface GiftModalProps {
  onClose: () => void;
  lootDrop?: { item: LootDropItem; rarity: string } | null;
  xpGained?: number;
}

interface GiftData {
  id?: string;
  type: string;
  title?: string;
  text?: string;
  question?: string;
  answer?: string;
  _ai_provider?: string;
}

const SEEN_GIFTS_KEY = '2enbota_seen_gift_ids';

const GIFT_META: Record<string, { emoji: string; badge: string; color: string }> = {
  super_joke: { emoji: '🎭', badge: 'Анекдот', color: '#ffb74d' },
  poem: { emoji: '📝', badge: 'Стишок-минутка', color: '#64b5f6' },
  riddle: { emoji: '🧩', badge: 'Загадка с подвохом', color: '#ba68c8' },
  meme: { emoji: '😂', badge: 'Мем от 2EnBota', color: '#4db6ac' },
  advice: { emoji: '💡', badge: 'Лайфхак енотов', color: '#ffd54f' },
  compliment: { emoji: '💐', badge: 'Комплимент', color: '#f06292' },
  fun_fact: { emoji: '🧠', badge: 'Интересный факт', color: '#81c784' },
  nothing: { emoji: '🤷', badge: 'Пустой сундук!', color: '#90a4ae' },
};

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const SLOT_EMOJIS: Record<string, string> = {
  HEAD: '🪖',
  BODY: '🛡️',
  HANDS: '🧤',
  FEET: '👢',
  RING: '💍',
  AMULET: '📿',
  WEAPON: '⚔️',
};

export function GiftModal({ onClose, lootDrop, xpGained }: GiftModalProps) {
  const [gift, setGift] = useState<GiftData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    let seenIds: string[] = [];
    try {
      const stored = localStorage.getItem(SEEN_GIFTS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          seenIds = parsed.filter(item => typeof item === 'string');
        }
      }
    } catch {
      // ignore
    }

    api.post<GiftData>('/ai/gift', { seen_ids: seenIds })
      .then(data => {
        setGift(data);
        if (data?.id) {
          try {
            const updated = [...seenIds.slice(-80), data.id];
            localStorage.setItem(SEEN_GIFTS_KEY, JSON.stringify(updated));
          } catch {
            // ignore
          }
        }
      })
      .catch(() => setGift({
        type: 'advice',
        title: '💡 Лайфхак от енотов',
        text: 'Продолжай учиться! 2EnBota 🦊🦝 верит в тебя и гордится каждым шагом! 💪',
      }))
      .finally(() => setIsLoading(false));
  }, []);

  const meta = gift ? (GIFT_META[gift.type] ?? { emoji: '🎁', badge: 'Подарок', color: '#ffd54f' }) : { emoji: '🎁', badge: 'Подарок', color: '#ffd54f' };
  const rarityColor = lootDrop ? (RARITY_COLORS[lootDrop.rarity] ?? '#9e9e9e') : '#9e9e9e';
  const displayEmoji = lootDrop ? (SLOT_EMOJIS[lootDrop.item.slot] ?? '🎁') : meta.emoji;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--theme-bg-elevated, #161622)',
          border: '1px solid var(--theme-border, rgba(255,255,255,0.12))',
          borderRadius: 20,
          padding: '28px 22px',
          maxWidth: 380,
          width: '100%',
          textAlign: 'center',
          animation: 'fadeIn 0.25s ease',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 12, lineHeight: 1 }}>{displayEmoji}</div>

        {/* Начисление XP за урок (если передано) */}
        {xpGained && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 14px',
            borderRadius: 14,
            background: 'rgba(245, 158, 11, 0.15)',
            border: '1px solid #f59e0b',
            color: '#f59e0b',
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 12,
          }}>
            <span>⭐</span>
            <span>+{xpGained} XP персонажу!</span>
          </div>
        )}

        {/* Рубрика / Бейдж */}
        <div style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: 12,
          background: `${meta.color}22`,
          color: meta.color,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          marginBottom: 14,
        }}>
          {meta.badge}
        </div>

        {isLoading ? (
          <div style={{ color: 'var(--theme-text-secondary)', fontSize: 15, padding: '20px 0' }}>
            Подбираем подарок... 🦊🦝
          </div>
        ) : (
          <>
            {/* Текст подарка / Загадка */}
            <div style={{
              fontSize: 15,
              color: 'var(--theme-text, #f0f0f5)',
              lineHeight: 1.6,
              marginBottom: 18,
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              maxHeight: 240,
              overflowY: 'auto',
              padding: '0 4px',
            }}>
              {gift?.question ? (
                <div>
                  <div style={{ marginBottom: 12, fontWeight: 500 }}>{gift.question}</div>
                  {!showAnswer ? (
                    <button
                      onClick={() => setShowAnswer(true)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: 'rgba(186, 104, 200, 0.15)',
                        border: '1px solid #ba68c8',
                        color: '#ce93d8',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      👁️ Показать ответ
                    </button>
                  ) : (
                    <div style={{
                      padding: '12px',
                      background: 'rgba(186, 104, 200, 0.12)',
                      border: '1px solid #ba68c8',
                      borderRadius: 10,
                      fontSize: 14,
                      color: '#e1bee7',
                      animation: 'fadeIn 0.2s ease',
                    }}>
                      <span style={{ fontWeight: 700, color: '#f3e5f5' }}>Ответ: </span>
                      {gift.answer}
                    </div>
                  )}
                </div>
              ) : (
                gift?.text
              )}
            </div>

            {/* Дроп RPG-предмета, если выпал */}
            {lootDrop && (
              <div style={{
                marginBottom: 18,
                padding: '14px 16px',
                background: 'rgba(255, 215, 0, 0.08)',
                border: `1px solid ${rarityColor}`,
                borderRadius: 12,
                textAlign: 'left',
              }}>
                <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  🎁 Вам выпал предмет:
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: rarityColor }}>
                  {lootDrop.item.name}
                </div>
                {lootDrop.item.name_ru && (
                  <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                    {lootDrop.item.name_ru}
                  </div>
                )}
                <div style={{ fontSize: 13, color: 'var(--theme-text, #f0f0f5)', marginTop: 4 }}>
                  {lootDrop.item.description_ru || lootDrop.item.description}
                </div>
                {lootDrop.item.flavor_text && (
                  <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--theme-text-secondary)', marginTop: 6, opacity: 0.8 }}>
                    «{lootDrop.item.flavor_text}»
                  </div>
                )}
              </div>
            )}

            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '13px',
                background: 'var(--theme-primary, #6c5ce7)',
                color: 'var(--theme-primary-text, #ffffff)',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
            >
              Спасибо! 🙏
            </button>
          </>
        )}
      </div>
    </div>
  );
}
